import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
    GroupEntity,
    GroupEntityCategoryEnum,
    GroupMemberEntityMemberTypeEnum,
    GroupMembersResponse,
} from '@interpals/api-web';
import i18next from 'i18next';

import api from 'api';
import { ReduxState } from 'redux/root-reducer';
import { createCustomToast } from 'services/toasts';
import { selectAccountId } from 'redux/account/selectors';
import { selectGroupMembers } from 'redux/group/selectors';

export const FETCH_GROUPS_MEMBERS_LIMIT = 15;

export const fetchGroup = createAsyncThunk<GroupEntity, string, { rejectValue: Response }>(
    'group/fetch',
    async (id, { rejectWithValue }) => {
        try {
            return await api.getGroup({ id });
        } catch (e) {
            return rejectWithValue(e as Response);
        }
    },
);

interface CreateEditGroupParams {
    id: string;
    name?: string;
    description?: string;
    category?: GroupEntityCategoryEnum;
    language?: string;
    city_id?: string;
    cover_id?: string;
}

interface EditGroupParams {
    params: CreateEditGroupParams;
    onSuccess?: () => void;
}

export const editGroup = createAsyncThunk<GroupEntity, EditGroupParams>('group/edit', async ({ params }) => {
    return api.updateGroup(params);
});

export const clearGroup = createAction('group/clear');

export const deleteGroup = createAsyncThunk<void, string>('group/delete', async (id, { dispatch }) => {
    await api.deleteGroup({ id });
    dispatch(clearGroup());
});

type GroupMemberFilterVal = 'admin' | 'moderator' | 'owner' | 'blocked';

export interface FetchGroupMembersParams {
    id: string;
    member_type?: GroupMemberFilterVal;
}

interface FetchGroupMembersResult {
    response: GroupMembersResponse;
    member_type?: GroupMemberFilterVal;
}

export const fetchGroupMembers = createAsyncThunk<
    FetchGroupMembersResult,
    FetchGroupMembersParams,
    { state: ReduxState }
>('group/fetch-members', async (params, { getState }) => {
    const members = selectGroupMembers(getState());

    const offset =
        params.member_type === 'admin'
            ? members.admin.length
            : params.member_type === 'blocked'
              ? members.blocked.length
              : members.all.length;

    return {
        response: await api.getGroupMembers({
            ...params,
            limit: FETCH_GROUPS_MEMBERS_LIMIT,
            offset: offset,
        }),
        member_type: params.member_type,
    };
});

interface SetGroupMemberTypeParams {
    id: string;
    user_id: string;
    member_type: GroupMemberEntityMemberTypeEnum;
}

interface SetGroupMemberResult {
    groupId: string;
    uid: string;
    member_type: GroupMemberEntityMemberTypeEnum;
    isUserSelf: boolean;
}

export const setGroupMemberType = createAsyncThunk<
    SetGroupMemberResult,
    SetGroupMemberTypeParams,
    { state: ReduxState }
>('group/set-member-type', async (params, { getState }) => {
    await api
        .setGroupMemberType(params)
        .then(
            ({ meta }) =>
                meta.requestStatus !== 'rejected' &&
                createCustomToast('success', `${i18next.t('Group.MemberStatusChange')}`),
        );

    return {
        groupId: params.id,
        uid: params.user_id,
        member_type: params.member_type,
        isUserSelf: selectAccountId(getState()) === params.user_id,
    };
});

export const groupSubscribe = createAsyncThunk<GroupEntity, string>('group/subscribe', async (id, { dispatch }) => {
    const group = await api.joinGroup({ id });
    createCustomToast('success', `${i18next.t('Toast.SearchGroups.Subscribe')}`);
    dispatch(clearGroupMembers);

    return group;
});

export const groupUnsubscribe = createAsyncThunk<GroupEntity, string>('group/unsubscribe', async (id, { dispatch }) => {
    const group = await api.leaveGroup({ id });
    createCustomToast('success', `${i18next.t('Toast.SearchGroups.Unsubscribe')}`);
    dispatch(clearGroupMembers);

    return group;
});

export const clearGroupMembers = createAction('group/clear-group-members');
